import React, { useState } from 'react';
import { User, Scissors, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { barbers, barbershopInfo } from '../data/mock';

const BarbersSection = () => {
  const [activeBarber, setActiveBarber] = useState(0);

  return (
    <section id="barbers" className="py-20 sm:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute bottom-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#c9a962]/5 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#c9a962]/3 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" dir="rtl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-px w-12 bg-[#c9a962]" />
            <User className="w-5 h-5 text-[#c9a962]" />
            <div className="h-px w-12 bg-[#c9a962]" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" data-testid="barbers-section-title">
            הספרים <span className="text-[#c9a962]">שלנו</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            שני ספרים מקצועיים לשירותכם - בחרו את הספר המתאים לכם
          </p>
        </div>

        {/* Barber Toggle */}
        <div className="flex justify-center gap-4 mb-12">
          {barbers.map((barber, index) => (
            <button
              key={barber.id}
              data-testid={`barber-tab-${barber.name}`}
              onClick={() => setActiveBarber(index)}
              className={`px-8 py-4 text-lg font-bold transition-all duration-300 border-2 ${
                activeBarber === index
                  ? 'bg-[#c9a962] text-slate-950 border-[#c9a962] shadow-lg shadow-[#c9a962]/20'
                  : 'bg-transparent text-[#c9a962] border-[#c9a962]/30 hover:border-[#c9a962]/60'
              }`}
            >
              <div className="flex items-center gap-2">
                <Scissors className="w-5 h-5" />
                {barber.name}
              </div>
            </button>
          ))}
        </div>

        {/* Both Barbers Side by Side on Desktop */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {barbers.map((barber, index) => (
            <div
              key={barber.id}
              data-testid={`barber-card-${barber.name}`}
              className={`relative bg-slate-900 border transition-all duration-500 ${
                activeBarber === index
                  ? 'border-[#c9a962]/50 shadow-2xl shadow-[#c9a962]/10 scale-[1.02]'
                  : 'border-slate-800 hover:border-slate-700'
              }`}
            >
              {/* Barber Header */}
              <div className="p-8 pb-6 border-b border-slate-800">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    activeBarber === index ? 'bg-[#c9a962]' : 'bg-slate-800'
                  }`}>
                    <User className={`w-8 h-8 ${activeBarber === index ? 'text-slate-950' : 'text-[#c9a962]'}`} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{barber.fullName}</h3>
                    <p className="text-[#c9a962] text-sm font-medium">{barber.role}</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {barber.description}
                </p>
              </div>

              {/* Services List */}
              <div className="p-8 pt-6">
                <h4 className="text-white font-bold mb-4 text-sm tracking-wider uppercase">מחירון</h4>
                <div className="space-y-3">
                  {barber.services.map((service, sIndex) => (
                    <div
                      key={sIndex}
                      data-testid={`barber-${barber.name}-service-${sIndex}`}
                      className="flex items-center justify-between py-3 border-b border-slate-800/50 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <Scissors className="w-4 h-4 text-[#c9a962]/50" />
                        <span className="text-slate-300">{service.name}</span>
                      </div>
                      <span className="text-[#c9a962] font-bold text-lg">
                        ₪{service.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Book CTA */}
              <div className="px-8 pb-8">
                <a
                  href={barbershopInfo.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    data-testid={`book-barber-${barber.name}`}
                    className={`w-full font-bold py-6 transition-all duration-300 ${
                      activeBarber === index
                        ? 'bg-[#c9a962] hover:bg-[#b8962f] text-slate-950'
                        : 'bg-slate-800 hover:bg-slate-700 text-white'
                    }`}
                  >
                    <Calendar className="w-5 h-5 ml-2" />
                    הזמן תור אצל {barber.name}
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BarbersSection;
